



























import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator'
import { ExStore } from 'vuex'
import { EventEmitter } from 'events'
import { LoginUser, Worker } from '@/types/user'
import WebSocketProvider from '@/websocket'
import { WebSocketClientInterface } from '@/websocket/types'
import * as receiveMessages from '@/websocket/receivers/call/receiveMessages'
import { WebSocketMessageMode } from '@/websocket/constants'
import { WebSocketCallMessageTypes } from '@/websocket/receivers/call'
import WebRTCProvider, { WebRTC, WebRTCEventTypes } from '@/webrtc'
import { isCordovaEnvironment, isPlatformIOS } from '@/utils/cordova'
import { User } from '@/types/user'

@Component({
  components: {
    VideoCallRoomContainer: () =>
      import('@/components/common/videoCallRoom/VideoCallRoomContainer.vue')
  }
})
export default class VideoCallContainer extends Vue {
  @Prop(Object) user!: LoginUser

  @Emit('created')
  emitCreated() {
    return
  }

  $store!: ExStore
  events = new EventEmitter()
  webSocket!: WebSocketClientInterface
  webrtc: WebRTC | null = null

  get inCallWebView() {
    return this.$store.getters['videoCallRoom/inCallWebView']
  }

  get isCalling() {
    return this.$store.getters['videoCallRoom/isCalling']
  }

  get isCaller() {
    return this.$store.getters['videoCallRoom/isCaller']
  }

  get roomId() {
    return this.$store.getters['videoCallRoom/roomId']
  }

  get pinCode() {
    return this.$store.getters['videoCallRoom/pinCode']
  }

  get roomSecret() {
    return this.$store.getters['videoCallRoom/roomSecret']
  }

  get workerInfo(): Worker | undefined {
    return this.$store.getters['worker/workers'][this.user.id]
  }

  get occupationCode() {
    const worker = this.workerInfo
    return worker ? worker.occupation.code : ''
  }

  get occupationShortName() {
    const worker = this.workerInfo
    return worker ? worker.occupation.shortName : ''
  }

  get requestPendingRoomId() {
    return this.$store.getters['videoCallRoom/requestPendingRoomId']
  }

  get participants() {
    return this.$store.getters['videoCallRoom/participants']
  }

  get pendingUsers() {
    return this.$store.getters['videoCallRoom/pendingUsers']
  }

  get deniedUsers() {
    return this.$store.getters['videoCallRoom/deniedUsers']
  }

  get caller() {
    return this.$store.getters['videoCallRoom/caller']
  }

  async created() {
    if (this.user.isWorker && !this.workerInfo) {
      await this.$store.dispatch('worker/addOne', { workerId: this.user.id })
    }
    this.webSocket = WebSocketProvider.createConnection()
    this.webSocket.subscribeMessage(
      WebSocketMessageMode.CALL,
      WebSocketCallMessageTypes.DENIED,
      this.handleRemoteDenied.bind(this)
    )
    this.webSocket.subscribeMessage(
      WebSocketMessageMode.CALL,
      WebSocketCallMessageTypes.CANCELED,
      this.handleRemoteCanceled.bind(this)
    )
    this.$store.commit('videoCallRoom/setVideoRoomReady', true)
    this.$postMessage.addEventListener('activateApp', () => {
      this.webSocket = WebSocketProvider.createConnection()
    })
    this.emitCreated()
  }

  destroyed() {
    this.$store.commit('videoCallRoom/setVideoRoomReady', false)
  }

  async handleDestroyed() {
    this.webSocket.send(WebSocketMessageMode.CALL, {
      type: WebSocketCallMessageTypes.CANCELED,
      roomId: this.roomId
    })

    await this.$store.dispatch('videoCallRoom/leave')

    WebRTCProvider.destroy()

    if (this.inCallWebView) {
      this.$router.replace({ name: 'Hangup' })
    }
  }

  /**
   * 発信処理
   */
  @Watch('isCalling')
  async handleCalling(isCalling: boolean) {
    if (!isCalling) {
      WebRTCProvider.destroy()
      return
    }

    if (!this.isCaller) {
      return
    }

    const to = Object.keys(this.pendingUsers).map(id => Number(id))

    if (isCordovaEnvironment() && !this.inCallWebView) {
      if (isPlatformIOS()) {
        if (this.user.isPatient || this.user.isPatientRelative) {
          this.$router.replace({ name: 'PatientHangupInner' })
        } else {
          this.$router.replace({ name: 'WorkerHangupInner' })
        }
      }
      console.log('%c isCordovaEnvironment %c', 'color:green;', '')
      this.$store.commit('videoCallRoom/switchCalling', false)
      const params = {
        isPatient: this.user.isPatient,
        isRelative: this.user.isPatientRelative,
        videoApiSecret: this.user.videoApiSecret,
        toUsers: Object.values(this.pendingUsers) as User[]
      }
      this.$postMessage.showCallDispatchView(params)
    } else {
      console.log('%c WebRTCProvider.createConnection %c', 'color:green;', '')
      const webrtc = await WebRTCProvider.createConnection(
        this.user.videoApiSecret
      )

      await new Promise(resolve => {
        webrtc.observe(WebRTCEventTypes.SESSION_SUCCEEDED, resolve)
      })

      const { roomSecret, roomId, pinCode } = await webrtc.createRoom()

      this.webSocket.send(WebSocketMessageMode.CALL, {
        type: WebSocketCallMessageTypes.REQUEST,
        secret: roomSecret,
        pin: pinCode,
        roomId,
        to
      })
      this.$store.commit('videoCallRoom/refreshRoom', {
        roomSecret,
        pinCode,
        roomId
      })
    }
  }

  /**
   * 受信拒否
   */
  async handleRemoteDenied(
    message: receiveMessages.ReceiveMessageBodyCallDenied
  ) {
    const { roomId, from } = message

    if (roomId !== this.roomId) {
      return
    }

    if (!this.isCalling) {
      return
    }

    if (from === this.user.id) {
      return
    }

    const user = this.pendingUsers[from]
    if (user) {
      this.$store.commit('videoCallRoom/addDeniedUser', user)
      this.$store.commit('videoCallRoom/removePendingUser', { id: from })
    }
  }

  /**
   * 切断
   */
  handleRemoteCanceled(
    message: receiveMessages.ReceiveMessageBodyCallCanceled
  ) {
    const { from } = message

    if (from === this.user.id) {
      return
    }

    if (this.caller && this.caller.id === from) {
      this.$store.commit('videoCallRoom/setCancelledByCaller', true)
    }

    if (!this.requestPendingRoomId) {
      return
    }

    if (!this.caller || this.caller.id !== from) {
      return
    }

    WebRTCProvider.destroy()

    if (this.inCallWebView) {
      this.$router.replace({ name: 'Hangup' })
    }
  }
}
